// frontend/src/pages/freelancer/FreelancerAnalytics.js
import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import FreelancerSidebarLayout from "./FreelancerSidebarLayout";
import axios from "axios";
import { Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "./freelancerStyles.css";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
);

// Maps canonical PayoutLedgerEntry fields → the booking-shaped objects the
// existing held/released render sections expect.
const normalizeCanonicalForFreelancer = (rec) => {
  const sl = (t) =>
    ({
      booking_service: "Service Booking",
      booking_product: "Product Booking",
      booth_rent: "Booth Rent",
      package_session: "Package — Session",
      manual: "Manual",
    })[t] ||
    t ||
    "Earning";
  const mapEntry = (e) => ({
    customerName: sl(e.sourceType),
    professionalEarnings: e.netEligibleAmount || 0,
    completedAt: e.createdAt,
    heldUntil: e.heldUntil,
    eligibleAt: e.eligibleAt,
  });
  return {
    held: (rec.held?.entries || []).map(mapEntry),
    released: (rec.released?.entries || []).map(mapEntry),
    nextPayoutDate: rec.nextPayoutDate,
    platformSettings: rec.platformSettings || {},
  };
};

function FreelancerAnalytics() {
  const navigate = useNavigate();
  const isBoothRenterRoute = window.location.pathname.includes(
    "/dashboard/booth-renter",
  );
  const roleCode = isBoothRenterRoute ? "R7" : "R4";
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("services"); // 'services' or 'products'

  // Earnings state
  const [earnings, setEarnings] = useState({
    thisMonth: 0,
    thisYear: 0,
    year: new Date().getFullYear(),
    pending: 0,
    paid: 0,
  });
  const [payouts, setPayouts] = useState([]);
  const [transactions, setTransactions] = useState([]);

  // NEW: Payout reconciliation state (like R3)
  const [payoutReconciliation, setPayoutReconciliation] = useState(null);
  const [showHeldSection, setShowHeldSection] = useState(true);
  const [showReleasedSection, setShowReleasedSection] = useState(true);

  // Service analytics state
  const [serviceAnalytics, setServiceAnalytics] = useState({
    totalBookings: 0,
    totalRevenue: 0,
    avgRating: 0,
    completionRate: 0,
    popularServices: [],
    revenueByMonth: [],
  });

  // Product analytics state
  const [productPerformance, setProductPerformance] = useState({
    summary: {},
    products: [],
  });
  const [inventoryReport, setInventoryReport] = useState({
    summary: {},
    products: {},
  });
  const [categoryPerformance, setCategoryPerformance] = useState({
    categories: [],
    totalRevenue: 0,
  });

  const [timeframe, setTimeframe] = useState("30days");
  const [errorMsg, setErrorMsg] = useState("");
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  const handleUnauthorized = useCallback(() => {
    localStorage.removeItem("ogestyle_token");
    navigate("/login");
  }, [navigate]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem("ogestyle_token");
    if (!token) {
      navigate("/login");
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  }, [navigate]);

  const getApiBaseUrl = useCallback(() => {
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    if (!baseURL) {
      setErrorMsg("Configuration error.");
      return null;
    }
    return baseURL;
  }, []);

  const fetchEarnings = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const [earningsRes, payoutsRes, reconciliationRes] = await Promise.all([
        axios.get(`${baseURL}/api/staff/earnings`, { headers }),
        axios.get(`${baseURL}/api/payout-ledger/staff/history`, { headers }),
        axios.get(`${baseURL}/api/payout-ledger/staff/reconciliation?role=${roleCode}`, { headers }),
      ]);

      setEarnings(
        earningsRes.data || {
          thisMonth: 0,
          thisYear: 0,
          year: new Date().getFullYear(),
          pending: 0,
          paid: 0,
        },
      );
      setPayouts(payoutsRes.data.payouts || []);
      setTransactions(earningsRes.data.transactions || []);
      const recData = reconciliationRes.data || null;
      setPayoutReconciliation(
        recData ? normalizeCanonicalForFreelancer(recData) : null,
      );
    } catch (err) {
      if (err?.response?.status === 401) {
        handleUnauthorized();
      } else {
        setErrorMsg(err?.response?.data?.message || "Failed to load earnings");
      }
    }
  }, [getAuthHeaders, getApiBaseUrl, handleUnauthorized, roleCode]);

  const fetchServiceAnalytics = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const res = await axios.get(
        `${baseURL}/api/freelancer/analytics?timeframe=${timeframe}`,
        { headers },
      );
      setServiceAnalytics(
        res.data || {
          totalBookings: 0,
          totalRevenue: 0,
          avgRating: 0,
          completionRate: 0,
          popularServices: [],
          revenueByMonth: [],
        },
      );
    } catch (err) {
      if (err?.response?.status === 401) {
        handleUnauthorized();
      } else {
        setErrorMsg("Failed to load service analytics");
      }
    }
  }, [timeframe, getAuthHeaders, getApiBaseUrl, handleUnauthorized]);

  const fetchProductAnalytics = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const baseURL = getApiBaseUrl();
      if (!headers || !baseURL) return;

      const [performanceRes, inventoryRes, categoryRes] = await Promise.all([
        axios.get(`${baseURL}/api/freelancer/analytics/product-performance`, {
          headers,
        }),
        axios.get(`${baseURL}/api/freelancer/analytics/inventory-report`, {
          headers,
        }),
        axios.get(`${baseURL}/api/freelancer/analytics/category-performance`, {
          headers,
        }),
      ]);

      setProductPerformance(
        performanceRes.data || { summary: {}, products: [] },
      );
      setInventoryReport(inventoryRes.data || { summary: {}, products: {} });
      setCategoryPerformance(
        categoryRes.data || { categories: [], totalRevenue: 0 },
      );
    } catch (err) {
      if (err?.response?.status === 401) {
        handleUnauthorized();
      } else {
        setErrorMsg("Failed to load product analytics");
      }
    }
  }, [getAuthHeaders, getApiBaseUrl, handleUnauthorized]);

  useEffect(() => {
    const fetchAllAnalytics = async () => {
      setLoading(true);
      await Promise.all([
        fetchEarnings(),
        fetchServiceAnalytics(),
        fetchProductAnalytics(),
      ]);
      setLoading(false);
    };
    fetchAllAnalytics();
  }, [fetchEarnings, fetchServiceAnalytics, fetchProductAnalytics, activeTab]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchEarnings();
      fetchServiceAnalytics();
      fetchProductAnalytics();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchEarnings, fetchServiceAnalytics, fetchProductAnalytics]);

  // Chart configurations
  const categoryChartData = {
    labels: categoryPerformance.categories.map((c) => c.category),
    datasets: [
      {
        label: "Revenue by Category",
        data: categoryPerformance.categories.map((c) => c.revenue),
        backgroundColor: [
          "rgba(54, 162, 235, 0.6)",
          "rgba(255, 99, 132, 0.6)",
          "rgba(255, 206, 86, 0.6)",
          "rgba(75, 192, 192, 0.6)",
          "rgba(153, 102, 255, 0.6)",
          "rgba(255, 159, 64, 0.6)",
        ],
        borderColor: [
          "rgba(54, 162, 235, 1)",
          "rgba(255, 99, 132, 1)",
          "rgba(255, 206, 86, 1)",
          "rgba(75, 192, 192, 1)",
          "rgba(153, 102, 255, 1)",
          "rgba(255, 159, 64, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  const inventoryChartData = {
    labels: ["In Stock", "Low Stock", "Out of Stock", "Inactive"],
    datasets: [
      {
        data: [
          inventoryReport.summary?.inStock || 0,
          inventoryReport.summary?.lowStock || 0,
          inventoryReport.summary?.outOfStock || 0,
          inventoryReport.summary?.inactive || 0,
        ],
        backgroundColor: [
          "rgba(16, 185, 129, 0.6)",
          "rgba(245, 158, 11, 0.6)",
          "rgba(239, 68, 68, 0.6)",
          "rgba(156, 163, 175, 0.6)",
        ],
        borderColor: [
          "rgba(16, 185, 129, 1)",
          "rgba(245, 158, 11, 1)",
          "rgba(239, 68, 68, 1)",
          "rgba(156, 163, 175, 1)",
        ],
        borderWidth: 1,
      },
    ],
  };

  if (loading)
    return (
      <FreelancerSidebarLayout>
        <div style={{ padding: "40px", textAlign: "center" }}>
          <p>Loading analytics...</p>
        </div>
      </FreelancerSidebarLayout>
    );

  return (
    <FreelancerSidebarLayout>
      <div
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          padding: isMobile ? "15px" : "20px",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            marginBottom: "20px",
            gap: "15px",
          }}
        >
          <div>
            <h2
              style={{
                margin: "0 0 8px 0",
                fontSize: isMobile ? "24px" : "28px",
              }}
            >
              📊 Performance Analytics
            </h2>
            <p
              style={{
                margin: 0,
                color: "#666",
                fontSize: isMobile ? "14px" : "16px",
              }}
            >
              Track your service and product performance metrics
            </p>
          </div>
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            style={{
              padding: isMobile ? "8px 12px" : "10px 15px",
              border: "1px solid #ddd",
              borderRadius: "8px",
              fontSize: isMobile ? "13px" : "14px",
              background: "#fff",
              cursor: "pointer",
              width: isMobile ? "100%" : "auto",
            }}
          >
            <option value="7days">Last 7 Days</option>
            <option value="30days">Last 30 Days</option>
            <option value="3months">Last 3 Months</option>
            <option value="year">This Year</option>
          </select>
        </div>

        {/* Tab Navigation */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            marginBottom: "20px",
            borderBottom: "2px solid #e5e7eb",
            flexWrap: "wrap",
          }}
        >
          <button
            onClick={() => setActiveTab("services")}
            style={{
              padding: isMobile ? "10px 18px" : "12px 24px",
              background: activeTab === "services" ? "#001d4a" : "transparent",
              color: activeTab === "services" ? "#fff" : "#666",
              border: "none",
              borderBottom:
                activeTab === "services"
                  ? "3px solid #001d4a"
                  : "3px solid transparent",
              cursor: "pointer",
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: "600",
              transition: "all 0.2s",
            }}
          >
            📊 Service Analytics
          </button>
          <button
            onClick={() => setActiveTab("products")}
            style={{
              padding: isMobile ? "10px 18px" : "12px 24px",
              background: activeTab === "products" ? "#001d4a" : "transparent",
              color: activeTab === "products" ? "#fff" : "#666",
              border: "none",
              borderBottom:
                activeTab === "products"
                  ? "3px solid #001d4a"
                  : "3px solid transparent",
              cursor: "pointer",
              fontSize: isMobile ? "14px" : "16px",
              fontWeight: "600",
              transition: "all 0.2s",
            }}
          >
            📦 Product Analytics
          </button>
        </div>

        {errorMsg && (
          <div
            style={{
              background: "#fff3f3",
              border: "1px solid #c00",
              borderRadius: "8px",
              padding: "15px",
              marginBottom: "20px",
            }}
          >
            <p style={{ margin: 0, color: "#c00" }}>{errorMsg}</p>
          </div>
        )}

        {/* Earnings Tab */}
        {activeTab === "earnings" && (
          <>
            {/* Earnings Summary Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "15px",
                marginBottom: "30px",
              }}
            >
              {[
                {
                  label: `${earnings.year || new Date().getFullYear()} Earnings`,
                  value: `$${earnings.thisYear?.toFixed(2) || "0.00"}`,
                  color: "#001d4a",
                  icon: "💰",
                },
                {
                  label: "This Month",
                  value: `$${earnings.thisMonth?.toFixed(2) || "0.00"}`,
                  color: "#10b981",
                  icon: "📈",
                },
                {
                  label: "Pending",
                  value: `$${earnings.pending?.toFixed(2) || "0.00"}`,
                  color: "#f59e0b",
                  icon: "⏳",
                },
                {
                  label: "Paid Out",
                  value: `$${earnings.paid?.toFixed(2) || "0.00"}`,
                  color: "#10b981",
                  icon: "✅",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="dashboard-card"
                  style={{
                    padding: isMobile ? "20px" : "25px",
                    textAlign: "center",
                    background: `${item.color}10`,
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? "28px" : "32px",
                      marginBottom: "8px",
                    }}
                  >
                    {item.icon}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "20px" : "24px",
                      fontWeight: "700",
                      color: item.color,
                      marginBottom: "5px",
                    }}
                  >
                    {item.value}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#666",
                    }}
                  >
                    {item.label}
                  </div>
                </div>
              ))}
            </div>

            {/* NEW: Held Bookings Section (R4 Upgrade) */}
            {payoutReconciliation?.held?.length > 0 && (
              <div
                className="dashboard-card"
                style={{
                  padding: isMobile ? "20px" : "25px",
                  marginBottom: "25px",
                  background: "#fef3c7",
                  border: "1px solid #fbbf24",
                }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: "16px",
                    fontSize: isMobile ? "18px" : "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowHeldSection(!showHeldSection)}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span>🔒</span> Held ({payoutReconciliation.held.length})
                  </span>
                  <span style={{ fontSize: "14px" }}>
                    {showHeldSection ? "▼" : "▶"}
                  </span>
                </h3>
                {showHeldSection && (
                  <>
                    <p
                      style={{
                        marginTop: 0,
                        marginBottom: "16px",
                        color: "#92400e",
                        fontSize: "14px",
                      }}
                    >
                      Earnings held in escrow. Released after safety period.
                    </p>
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: "8px",
                        padding: isMobile ? "12px" : "16px",
                      }}
                    >
                      {payoutReconciliation.held.map((booking, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: "12px",
                            borderBottom:
                              idx < payoutReconciliation.held.length - 1
                                ? "1px solid #e5e7eb"
                                : "none",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "4px",
                            }}
                          >
                            <span
                              style={{ fontWeight: "600", fontSize: "15px" }}
                            >
                              {booking.customerName || "Customer"}
                            </span>
                            <span
                              style={{ fontWeight: "700", color: "#f59e0b" }}
                            >
                              $
                              {(
                                Number(booking.professionalEarnings) || 0
                              ).toFixed(2)}
                            </span>
                          </div>
                          <div style={{ fontSize: "13px", color: "#666" }}>
                            Completed:{" "}
                            {new Date(booking.completedAt).toLocaleDateString()}
                          </div>
                          {booking.eligibleAt || booking.paidOutAt ? (
                            <div
                              style={{
                                fontSize: "12px",
                                color: "#92400e",
                                marginTop: "4px",
                              }}
                            >
                              Payout date:{" "}
                              {new Date(
                                booking.eligibleAt || booking.paidOutAt,
                              ).toLocaleDateString()}
                            </div>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* NEW: Released Bookings Section (R4 Upgrade) */}
            {payoutReconciliation?.released?.length > 0 && (
              <div
                className="dashboard-card"
                style={{
                  padding: isMobile ? "20px" : "25px",
                  marginBottom: "25px",
                  background: "#dbeafe",
                  border: "1px solid #3b82f6",
                }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: "16px",
                    fontSize: isMobile ? "18px" : "20px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                  onClick={() => setShowReleasedSection(!showReleasedSection)}
                >
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span>📤</span> Released (
                    {payoutReconciliation.released.length})
                  </span>
                  <span style={{ fontSize: "14px" }}>
                    {showReleasedSection ? "▼" : "▶"}
                  </span>
                </h3>
                {showReleasedSection && (
                  <>
                    <p
                      style={{
                        marginTop: 0,
                        marginBottom: "16px",
                        color: "#1e40af",
                        fontSize: "14px",
                      }}
                    >
                      Ready for payout.{" "}
                      {payoutReconciliation.platformSettings
                        ?.instantPayoutEnabled
                        ? "Request instant payout or wait for scheduled transfer."
                        : "Will be paid on next scheduled payout."}
                    </p>
                    {payoutReconciliation.nextPayoutDate && (
                      <div
                        style={{
                          background: "#eff6ff",
                          padding: "12px",
                          borderRadius: "8px",
                          marginBottom: "16px",
                          fontSize: "14px",
                          color: "#1e40af",
                        }}
                      >
                        📅 Next scheduled payout:{" "}
                        <strong>
                          {new Date(
                            payoutReconciliation.nextPayoutDate,
                          ).toLocaleDateString()}
                        </strong>
                      </div>
                    )}
                    <div
                      style={{
                        background: "#fff",
                        borderRadius: "8px",
                        padding: isMobile ? "12px" : "16px",
                      }}
                    >
                      {payoutReconciliation.released.map((booking, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: "12px",
                            borderBottom:
                              idx < payoutReconciliation.released.length - 1
                                ? "1px solid #e5e7eb"
                                : "none",
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginBottom: "4px",
                            }}
                          >
                            <span
                              style={{ fontWeight: "600", fontSize: "15px" }}
                            >
                              {booking.customerName || "Customer"}
                            </span>
                            <span
                              style={{ fontWeight: "700", color: "#10b981" }}
                            >
                              $
                              {(
                                Number(booking.professionalEarnings) || 0
                              ).toFixed(2)}
                            </span>
                          </div>
                          <div style={{ fontSize: "13px", color: "#666" }}>
                            Released:{" "}
                            {new Date(
                              booking.payoutReleasedAt,
                            ).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Recent Payouts */}
            <div
              className="dashboard-card"
              style={{
                padding: isMobile ? "20px" : "25px",
                marginBottom: "25px",
              }}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: "20px",
                  fontSize: isMobile ? "18px" : "20px",
                }}
              >
                Recent Payouts
              </h3>
              {payouts.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "30px",
                    color: "#999",
                  }}
                >
                  <div style={{ fontSize: "36px", marginBottom: "10px" }}>
                    💳
                  </div>
                  <p>No payouts yet</p>
                </div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: isMobile ? "13px" : "14px",
                    }}
                  >
                    <thead>
                      <tr style={{ borderBottom: "2px solid #e0e0e0" }}>
                        <th
                          style={{
                            padding: isMobile ? "10px 8px" : "12px",
                            textAlign: "left",
                            fontWeight: "600",
                          }}
                        >
                          Date
                        </th>
                        <th
                          style={{
                            padding: isMobile ? "10px 8px" : "12px",
                            textAlign: "left",
                            fontWeight: "600",
                          }}
                        >
                          Amount
                        </th>
                        <th
                          style={{
                            padding: isMobile ? "10px 8px" : "12px",
                            textAlign: "left",
                            fontWeight: "600",
                          }}
                        >
                          Status
                        </th>
                        {!isMobile && (
                          <th
                            style={{
                              padding: "12px",
                              textAlign: "left",
                              fontWeight: "600",
                            }}
                          >
                            Method
                          </th>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {payouts.slice(0, 5).map((payout, idx) => {
                        // Determine badge color based on status
                        const isCompleted =
                          payout.status?.toLowerCase() === "completed";
                        const isInstant = payout.status
                          ?.toLowerCase()
                          .includes("instant");
                        const badgeColor = isCompleted
                          ? "#10b981"
                          : isInstant
                            ? "#3b82f6"
                            : "#f59e0b";
                        const badgeBg = isCompleted
                          ? "#10b98120"
                          : isInstant
                            ? "#3b82f620"
                            : "#f59e0b20";

                        return (
                          <tr
                            key={idx}
                            style={{ borderBottom: "1px solid #f0f0f0" }}
                          >
                            <td
                              style={{
                                padding: isMobile ? "10px 8px" : "12px",
                              }}
                            >
                              {new Date(payout.date).toLocaleDateString()}
                            </td>
                            <td
                              style={{
                                padding: isMobile ? "10px 8px" : "12px",
                                fontWeight: "600",
                                color: "#001d4a",
                              }}
                            >
                              ${payout.amount?.toFixed(2) || "0.00"}
                            </td>
                            <td
                              style={{
                                padding: isMobile ? "10px 8px" : "12px",
                              }}
                            >
                              <span
                                style={{
                                  padding: "3px 10px",
                                  borderRadius: "12px",
                                  background: badgeBg,
                                  color: badgeColor,
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  textTransform: "capitalize",
                                }}
                              >
                                {payout.status}
                              </span>
                            </td>
                            {!isMobile && (
                              <td style={{ padding: "12px", color: "#666" }}>
                                {payout.method || "Bank Transfer"}
                              </td>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Recent Transactions */}
            <div
              className="dashboard-card"
              style={{ padding: isMobile ? "20px" : "25px" }}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: "20px",
                  fontSize: isMobile ? "18px" : "20px",
                }}
              >
                Recent Transactions
              </h3>
              {transactions.length === 0 ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "30px",
                    color: "#999",
                  }}
                >
                  <div style={{ fontSize: "36px", marginBottom: "10px" }}>
                    📝
                  </div>
                  <p>No transactions yet</p>
                </div>
              ) : (
                <div style={{ display: "grid", gap: "12px" }}>
                  {transactions.slice(0, 10).map((txn, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: isMobile ? "12px" : "15px",
                        border: "1px solid #e0e0e0",
                        borderRadius: "8px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexWrap: isMobile ? "wrap" : "nowrap",
                        gap: "10px",
                      }}
                    >
                      <div
                        style={{
                          flex: 1,
                          minWidth: isMobile ? "100%" : "auto",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: "600",
                            fontSize: isMobile ? "14px" : "15px",
                            marginBottom: "4px",
                          }}
                        >
                          {txn.description || "Booking Payment"}
                        </div>
                        <div
                          style={{
                            fontSize: isMobile ? "12px" : "13px",
                            color: "#666",
                          }}
                        >
                          {new Date(txn.date).toLocaleDateString()} at{" "}
                          {new Date(txn.date).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                        </div>
                      </div>
                      <div
                        style={{
                          fontSize: isMobile ? "16px" : "18px",
                          fontWeight: "700",
                          color: txn.type === "credit" ? "#10b981" : "#ef4444",
                        }}
                      >
                        {txn.type === "credit" ? "+" : "-"}$
                        {txn.amount?.toFixed(2) || "0.00"}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </>
        )}

        {/* Service Analytics Tab */}
        {activeTab === "services" && (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "15px",
                marginBottom: "30px",
              }}
            >
              {[
                {
                  label: "Services Performed",
                  value: serviceAnalytics.totalBookings || 0,
                  color: "#001d4a",
                  icon: "📅",
                },
                {
                  label: "Total Revenue",
                  value: `$${serviceAnalytics.totalRevenue?.toFixed(2) || "0.00"}`,
                  color: "#10b981",
                  icon: "💰",
                },
                {
                  label: "Avg Rating",
                  value: `${serviceAnalytics.avgRating?.toFixed(1) || "0.0"} ★`,
                  color: "#f59e0b",
                  icon: "⭐",
                },
                {
                  label: "Completion Rate",
                  value: `${serviceAnalytics.completionRate || 0}%`,
                  color: "#6366f1",
                  icon: "✔️",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="dashboard-card"
                  style={{
                    padding: isMobile ? "20px" : "25px",
                    textAlign: "center",
                    background: `${item.color}10`,
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? "28px" : "32px",
                      marginBottom: "8px",
                    }}
                  >
                    {item.icon}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "20px" : "24px",
                      fontWeight: "700",
                      color: item.color,
                      marginBottom: "5px",
                    }}
                  >
                    {item.value}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#666",
                    }}
                  >
                    {item.label}
                  </div>
                </div>
              ))}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: "20px",
              }}
            >
              <div
                className="dashboard-card"
                style={{ padding: isMobile ? "20px" : "25px" }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: "20px",
                    fontSize: isMobile ? "18px" : "20px",
                  }}
                >
                  Popular Services
                </h3>
                {serviceAnalytics.popularServices?.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "30px",
                      color: "#999",
                    }}
                  >
                    <div style={{ fontSize: "36px", marginBottom: "10px" }}>
                      📊
                    </div>
                    <p>No data yet</p>
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: "12px" }}>
                    {serviceAnalytics.popularServices
                      ?.slice(0, 5)
                      .map((service, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: isMobile ? "12px" : "15px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div>
                            <div
                              style={{
                                fontWeight: "600",
                                fontSize: isMobile ? "14px" : "15px",
                                marginBottom: "4px",
                              }}
                            >
                              {service.name}
                            </div>
                            <div
                              style={{
                                fontSize: isMobile ? "12px" : "13px",
                                color: "#666",
                              }}
                            >
                              {service.bookings} bookings
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: isMobile ? "16px" : "18px",
                              fontWeight: "700",
                              color: "#10b981",
                            }}
                          >
                            ${service.revenue?.toFixed(2) || "0.00"}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>

              <div
                className="dashboard-card"
                style={{ padding: isMobile ? "20px" : "25px" }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: "20px",
                    fontSize: isMobile ? "18px" : "20px",
                  }}
                >
                  Revenue Trend
                </h3>
                {serviceAnalytics.revenueByMonth?.length === 0 ? (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "30px",
                      color: "#999",
                    }}
                  >
                    <div style={{ fontSize: "36px", marginBottom: "10px" }}>
                      📈
                    </div>
                    <p>No data yet</p>
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: "12px" }}>
                    {serviceAnalytics.revenueByMonth
                      ?.slice(0, 6)
                      .map((month, idx) => (
                        <div
                          key={idx}
                          style={{
                            padding: isMobile ? "12px" : "15px",
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: "600",
                              fontSize: isMobile ? "14px" : "15px",
                            }}
                          >
                            {month.month}
                          </div>
                          <div
                            style={{
                              fontSize: isMobile ? "16px" : "18px",
                              fontWeight: "700",
                              color: "#001d4a",
                            }}
                          >
                            ${month.revenue?.toFixed(2) || "0.00"}
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </>
        )}

        {/* Product Analytics Tab */}
        {activeTab === "products" && (
          <>
            {/* Product Summary Cards */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "1fr"
                  : "repeat(auto-fit, minmax(200px, 1fr))",
                gap: "15px",
                marginBottom: "30px",
              }}
            >
              {[
                {
                  label: "Total Products",
                  value: productPerformance.summary?.totalProducts || 0,
                  color: "#001d4a",
                  icon: "📦",
                },
                {
                  label: "Stock Value",
                  value: `$${productPerformance.summary?.totalStockValue?.toFixed(2) || "0.00"}`,
                  color: "#10b981",
                  icon: "💵",
                },
                {
                  label: "Revenue (30d)",
                  value: `$${productPerformance.summary?.totalRevenue?.toFixed(2) || "0.00"}`,
                  color: "#6366f1",
                  icon: "💰",
                },
                {
                  label: "Low Stock Items",
                  value: productPerformance.summary?.lowStockCount || 0,
                  color: "#f59e0b",
                  icon: "⚠️",
                },
              ].map((item, idx) => (
                <div
                  key={idx}
                  className="dashboard-card"
                  style={{
                    padding: isMobile ? "20px" : "25px",
                    textAlign: "center",
                    background: `${item.color}10`,
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? "28px" : "32px",
                      marginBottom: "8px",
                    }}
                  >
                    {item.icon}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "20px" : "24px",
                      fontWeight: "700",
                      color: item.color,
                      marginBottom: "5px",
                    }}
                  >
                    {item.value}
                  </div>
                  <div
                    style={{
                      fontSize: isMobile ? "12px" : "13px",
                      color: "#666",
                    }}
                  >
                    {item.label}
                  </div>
                </div>
              ))}
            </div>

            {/* Charts */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
                gap: "20px",
                marginBottom: "30px",
              }}
            >
              <div
                className="dashboard-card"
                style={{ padding: isMobile ? "20px" : "25px" }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: "20px",
                    fontSize: isMobile ? "18px" : "20px",
                  }}
                >
                  Revenue by Category
                </h3>
                {categoryPerformance.categories.length > 0 ? (
                  <Doughnut
                    data={categoryChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: { legend: { position: "bottom" } },
                    }}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "30px",
                      color: "#999",
                    }}
                  >
                    <div style={{ fontSize: "36px", marginBottom: "10px" }}>
                      📊
                    </div>
                    <p>No sales data yet</p>
                  </div>
                )}
              </div>

              <div
                className="dashboard-card"
                style={{ padding: isMobile ? "20px" : "25px" }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: "20px",
                    fontSize: isMobile ? "18px" : "20px",
                  }}
                >
                  Inventory Status
                </h3>
                {inventoryReport.summary?.totalProducts > 0 ? (
                  <Doughnut
                    data={inventoryChartData}
                    options={{
                      responsive: true,
                      maintainAspectRatio: true,
                      plugins: { legend: { position: "bottom" } },
                    }}
                  />
                ) : (
                  <div
                    style={{
                      textAlign: "center",
                      padding: "30px",
                      color: "#999",
                    }}
                  >
                    <div style={{ fontSize: "36px", marginBottom: "10px" }}>
                      📦
                    </div>
                    <p>No products yet</p>
                  </div>
                )}
              </div>
            </div>

            {/* Top Products Table */}
            <div
              className="dashboard-card"
              style={{ padding: isMobile ? "20px" : "25px" }}
            >
              <h3
                style={{
                  marginTop: 0,
                  marginBottom: "20px",
                  fontSize: isMobile ? "18px" : "20px",
                }}
              >
                Top Products (Last 30 Days)
              </h3>
              {productPerformance.products?.length > 0 ? (
                <div style={{ overflowX: "auto" }}>
                  <table
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      fontSize: isMobile ? "13px" : "14px",
                    }}
                  >
                    <thead>
                      <tr
                        style={{
                          borderBottom: "2px solid #e5e7eb",
                          textAlign: "left",
                        }}
                      >
                        <th style={{ padding: "12px 8px", fontWeight: "600" }}>
                          Product
                        </th>
                        <th
                          style={{
                            padding: "12px 8px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          Stock
                        </th>
                        <th
                          style={{
                            padding: "12px 8px",
                            fontWeight: "600",
                            textAlign: "center",
                          }}
                        >
                          Sold
                        </th>
                        <th
                          style={{
                            padding: "12px 8px",
                            fontWeight: "600",
                            textAlign: "right",
                          }}
                        >
                          Revenue
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {productPerformance.products
                        .slice(0, 10)
                        .map((product, idx) => (
                          <tr
                            key={idx}
                            style={{ borderBottom: "1px solid #f3f4f6" }}
                          >
                            <td style={{ padding: "12px 8px" }}>
                              <div style={{ fontWeight: "600" }}>
                                {product.name}
                              </div>
                              <div style={{ fontSize: "12px", color: "#666" }}>
                                {product.category}
                              </div>
                            </td>
                            <td
                              style={{
                                padding: "12px 8px",
                                textAlign: "center",
                              }}
                            >
                              <span
                                style={{
                                  padding: "4px 8px",
                                  borderRadius: "4px",
                                  background:
                                    product.stock < 5 ? "#fef3c7" : "#d1fae5",
                                  color:
                                    product.stock < 5 ? "#92400e" : "#065f46",
                                  fontSize: "13px",
                                  fontWeight: "600",
                                }}
                              >
                                {product.stock}
                              </span>
                            </td>
                            <td
                              style={{
                                padding: "12px 8px",
                                textAlign: "center",
                                fontWeight: "600",
                              }}
                            >
                              {product.soldLast30Days}
                            </td>
                            <td
                              style={{
                                padding: "12px 8px",
                                textAlign: "right",
                                fontWeight: "700",
                                color: "#10b981",
                              }}
                            >
                              ${product.revenue?.toFixed(2) || "0.00"}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "30px",
                    color: "#999",
                  }}
                >
                  <div style={{ fontSize: "36px", marginBottom: "10px" }}>
                    📦
                  </div>
                  <p>No product data yet</p>
                </div>
              )}
            </div>

            {/* Alerts Section */}
            {(inventoryReport.products?.lowStock?.length > 0 ||
              inventoryReport.products?.outOfStock?.length > 0) && (
              <div
                className="dashboard-card"
                style={{
                  padding: isMobile ? "20px" : "25px",
                  background: "#fff7ed",
                  border: "1px solid #fed7aa",
                }}
              >
                <h3
                  style={{
                    marginTop: 0,
                    marginBottom: "15px",
                    fontSize: isMobile ? "16px" : "18px",
                    color: "#c2410c",
                  }}
                >
                  ⚠️ Inventory Alerts
                </h3>
                {inventoryReport.products?.lowStock?.length > 0 && (
                  <div style={{ marginBottom: "15px" }}>
                    <p style={{ fontWeight: "600", marginBottom: "8px" }}>
                      Low Stock ({inventoryReport.products.lowStock.length}):
                    </p>
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                    >
                      {inventoryReport.products.lowStock.map((product, idx) => (
                        <span
                          key={idx}
                          style={{
                            padding: "6px 12px",
                            background: "#fef3c7",
                            borderRadius: "6px",
                            fontSize: "13px",
                            color: "#92400e",
                          }}
                        >
                          {product.name} ({product.stock} left)
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {inventoryReport.products?.outOfStock?.length > 0 && (
                  <div>
                    <p style={{ fontWeight: "600", marginBottom: "8px" }}>
                      Out of Stock ({inventoryReport.products.outOfStock.length}
                      ):
                    </p>
                    <div
                      style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}
                    >
                      {inventoryReport.products.outOfStock.map(
                        (product, idx) => (
                          <span
                            key={idx}
                            style={{
                              padding: "6px 12px",
                              background: "#fee2e2",
                              borderRadius: "6px",
                              fontSize: "13px",
                              color: "#991b1b",
                            }}
                          >
                            {product.name}
                          </span>
                        ),
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </FreelancerSidebarLayout>
  );
}

export default FreelancerAnalytics;
